import { Injectable, inject } from '@angular/core'
import { AddFieldComponent } from '@app/feature/add-field/add-field.component'
import { ThemePalettes } from '@components-library/models'
import { ConfirmationDialogService } from '@components-library/services/confirmation-dialog.service'
import { CommonDialogResultStatus } from '@components-library/tb-confirmation-popup/tb-confirmation-dialog.component'
import { ModalFlowManagerService } from '@components-library/tb-modal-manager/modal-flow-manager.service'
import { ModalManagerService } from '@components-library/tb-modal-manager/modal-manager.service'
import { AddFieldData, Field } from '@models/ui'
import { FieldFacadeService } from '@services/store-facade'
import { UiFacadeService } from '@services/store-facade/ui-facade.service'
import { EMPTY, finalize } from 'rxjs'
import { switchMap } from 'rxjs/operators'

@Injectable({ providedIn: 'root' })
export class ManageFieldsService {
    private readonly modalFlowManagerService = inject(ModalFlowManagerService)
    private readonly confirmationPopupService = inject(ConfirmationDialogService)
    private readonly fieldFacadeService = inject(FieldFacadeService)
    private readonly uiFacadeService = inject(UiFacadeService)

    addField(data: AddFieldData, modalManagerService?: ModalManagerService) {
        const service = modalManagerService ?? this.modalFlowManagerService
        return service.openDialog<AddFieldComponent, AddFieldData, Field | undefined>({
            component: AddFieldComponent,
            data,
        })
    }

    editField(field: Field) {
        this.openEditFieldDialog(field)
    }

    duplicateField(field: Field) {
        this.openEditFieldDialog({
            ...field,
            name: field.name + ' copy',
            duplicate: true,
        })
    }

    deleteField(guid: string, showLoaderOnConfirm: boolean = false) {
        return this.confirmationPopupService
            .openCommon({
                translations: {
                    title: 'manage_fields.delete_field_conf_title',
                    message: 'manage_fields.delete_field_conf_message',
                    confirm: 'manage_fields.delete_field_conf_button',
                },
                colors: {
                    confirm: ThemePalettes.secondary,
                },
            })
            .pipe(
                switchMap((status: CommonDialogResultStatus) => {
                    if (status === CommonDialogResultStatus.CONFIRM) {
                        if (showLoaderOnConfirm) {
                            this.uiFacadeService.showViewLoader()
                        }
                        return this.fieldFacadeService.deleteField(guid)
                    }
                    return EMPTY
                }),
                finalize(() => {
                    if (showLoaderOnConfirm) {
                        this.uiFacadeService.hideViewLoader()
                    }
                }),
            )
    }

    private openEditFieldDialog(data: AddFieldData) {
        this.modalFlowManagerService.openDialog<AddFieldComponent, AddFieldData>({
            component: AddFieldComponent,
            data,
        })
    }
}
